/**
 * QueueItemRow component.
 *
 * Renders a collapsed queue item for both attention items (primary) and
 * approval items (history tab). Displays:
 * - Attention type badge with color
 * - Title and subtitle
 * - Relative time since created
 * - Customer display (when available)
 * - Operator assignment chip
 * - Context-sensitive action buttons
 */

import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react'
import { ChevronDown, Ban, RotateCcw, Loader2 } from 'lucide-react'
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/shared/components/ui'
import { ConfirmModal } from '@/shared/components/ConfirmModal'
import { useToast } from '@/shared/hooks'
import { cn } from '@/lib/utils'
import { getToolIcon } from '../utils/icons'
import { retryExecution } from '@/features/automations/api'
import { useOperator } from '@/features/auth'
import { useQueueStore } from '../store'
import type { AttentionItem, ApprovalItem, AttentionQueueTab, OperatorTag } from '../types'
import {
  getAttentionTypeLabel,
  getAttentionTypeBadgeColor,
  getCustomerDisplay,
  getAttentionId,
  OPERATOR_TAGS,
} from '../types'
import { IdBadge } from './approval/renderers/typeAware/microRenderers'
import { StarRating } from './StarRating'
import { buildRowPreview } from '../buildRowPreview'
import { getAppLabel } from '../utils/appLabel'
import { ASSIGNMENT_ENABLED } from '../featureFlags'
import { useSessionsStore } from '@/features/sessions/store'

/**
 * Fade-out animation duration before the row is removed from the
 * queue. Matches the `slideOutLeft` keyframe used by
 * `queue-item-fading-out` and the SSE `item_resolved` handler.
 */
const ROW_FADE_OUT_MS = 300

/**
 * Maximum number of turns the AgentSessionModal renders. Mirrored here so
 * the row's hover-prefetch matches the modal's initial-load size — that
 * way a hover-then-click flow only triggers a single network request.
 *
 * Keep in sync with `TURN_CAP` in
 * `apps/stream/src/features/queue/components/AgentSessionModal/AgentSessionModalBody.tsx`.
 */
const HOVER_PREFETCH_LIMIT = 200

/**
 * Debounce window for hover-prefetch. Hovering through a list of rows on
 * the way to a target row shouldn't fire a request for every row the
 * cursor passes over — only stick if the user pauses long enough to
 * suggest intent.
 */
const HOVER_PREFETCH_DELAY_MS = 150

/**
 * Clean up title by removing redundant prefixes that are already shown in the type badge.
 * For example, if the badge shows "TOOL APPROVAL", remove "Approve: " or "Approval: " from the title.
 */
function cleanTitle(title: string, attentionType: string): string {
  let cleaned = title

  // Remove "Approve: " or "Approval: " prefix for approval types (already shown in badge)
  if (attentionType.includes('approval')) {
    if (cleaned.startsWith('Approve: ')) {
      cleaned = cleaned.slice('Approve: '.length)
    } else if (cleaned.startsWith('Approval: ')) {
      cleaned = cleaned.slice('Approval: '.length)
    }
  }

  // Remove "Review: " prefix for review types
  if (attentionType.includes('review') && cleaned.startsWith('Review: ')) {
    cleaned = cleaned.slice('Review: '.length)
  }

  return cleaned
}

// ============================================
// Attention Item Row (primary)
// ============================================

export interface AttentionItemRowProps {
  /** The attention item to render. */
  item: AttentionItem
  /** Current queue tab. */
  tab: AttentionQueueTab
  /** Current operator's assignment tag. */
  operatorTag?: OperatorTag | null
  /** The assignment tag for this item (from assignments map). */
  assignedTo?: string | null
  /** Whether this item is currently expanded. */
  isExpanded?: boolean
  /** Whether this item is selected via keyboard navigation. */
  isKeyboardSelected?: boolean
  /** Whether this item just appeared (triggers highlight animation). */
  isNew?: boolean
  /** Whether this item is fading out (resolved). */
  isFadingOut?: boolean
  /** Called when the row body is clicked. */
  onItemClick?: () => void
  /** Called when assign action is triggered. */
  onAssign?: (tag: OperatorTag) => void
  /** Called when unassign action is triggered. */
  onUnassign?: () => void
  /** Whether to show resolution metadata (for history items). */
  showResolution?: boolean
  /** Called when the quick-suppress button is clicked. */
  onSuppress?: () => void
  /** Whether a suppress action is pending for this item. */
  suppressPending?: boolean
}

/**
 * Queue item row for unified attention items.
 */
export function AttentionItemRow({
  item,
  tab: _tab,
  operatorTag: _operatorTag,
  assignedTo,
  isExpanded,
  isKeyboardSelected,
  isNew,
  isFadingOut,
  onItemClick,
  onAssign,
  onUnassign,
  showResolution,
  onSuppress,
  suppressPending,
}: AttentionItemRowProps) {
  const badgeColor = getAttentionTypeBadgeColor(item.attention_type)
  const typeLabel = getAttentionTypeLabel(item.attention_type)
  const customer = getCustomerDisplay(item)

  // Which app this item acts on (e.g. a review reply for "My Timetable").
  // Sourced from the tool-call args app_id and lifted onto the row because
  // it's key triage context — without it the operator can't tell which app a
  // review reply is for without expanding the card. Null for tools that don't
  // target an app, in which case the badge is skipped entirely.
  const appLabel = getAppLabel(item)

  // Tool-aware preview shown on the collapsed row so the operator can triage
  // without expanding. For post_review_reply this surfaces the reviewer name,
  // star rating, and a truncated excerpt of the original review. Returns null
  // when there's nothing useful to surface — the block is then skipped so row
  // heights stay bounded. Hidden on resolved history rows (resolution metadata
  // takes that space instead).
  const preview =
    showResolution || item.resolution ? null : buildRowPreview(item)
  const hasPreview =
    preview !== null &&
    (preview.rating !== null ||
      preview.reviewerName !== null ||
      preview.headline !== null ||
      preview.body !== null)

  // ── Hover-prefetch the agent session transcript ────────────────────────────
  // The AgentSessionModal that opens from this row's inspector takes 5–10s
  // to fully populate when sessions have many turns, because it paginates
  // 50 turns at a time. Pre-fetching on row hover means by the time the
  // operator clicks "View Agent Session", the data is already in the
  // sessions-store cache and the modal opens instantly.
  //
  // The store's `loadTranscriptChunk` is idempotent — if the cache is
  // already loading or complete it returns without firing a request, so
  // hovering the same row repeatedly only ever produces one network call.
  const loadTranscriptChunk = useSessionsStore((s) => s.loadTranscriptChunk)
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleMouseEnter = useCallback(() => {
    if (!item.session_id) return
    // Debounce to avoid firing for every row the cursor passes over while
    // the operator is en route to a specific row.
    hoverTimerRef.current = setTimeout(() => {
      loadTranscriptChunk(item.session_id!, HOVER_PREFETCH_LIMIT)
    }, HOVER_PREFETCH_DELAY_MS)
  }, [item.session_id, loadTranscriptChunk])

  const handleMouseLeave = useCallback(() => {
    if (hoverTimerRef.current !== null) {
      clearTimeout(hoverTimerRef.current)
      hoverTimerRef.current = null
    }
  }, [])

  // Cancel any pending hover-prefetch on unmount so the queue can refresh
  // (which unmounts/remounts rows) without leaking timers or firing
  // delayed prefetches against stale session ids.
  useEffect(() => {
    return () => {
      if (hoverTimerRef.current !== null) {
        clearTimeout(hoverTimerRef.current)
        hoverTimerRef.current = null
      }
    }
  }, [])

  return (
    <div
      data-testid={`queue-item-${item.id}`}
      style={{
        ...styles.container,
        borderLeftColor: badgeColor,
        opacity: isFadingOut ? 0.5 : 1,
      }}
      className={cn(
        'queue-item-row',
        isExpanded && 'queue-item-expanded',
        isKeyboardSelected && !isExpanded && 'queue-item-keyboard-selected',
        isNew && 'queue-item-new',
        !isExpanded && 'queue-item-hoverable'
      )}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('button')) return
        onItemClick?.()
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onItemClick?.()
        }
      }}
    >
      {/* Top row: type badge, title, time */}
      <div style={styles.topRow}>
        <div style={styles.leftSection}>
          <Badge
            variant="outline"
            style={{
              ...styles.typeBadge,
              borderColor: badgeColor,
              color: badgeColor,
            }}
          >
            {typeLabel}
          </Badge>
          <span style={styles.title}>{cleanTitle(item.title, item.attention_type)}</span>
        </div>
        <div style={styles.rightSection}>
          {/* For resolved history items show when the decision was taken
              (resolved_at) rather than when the item was surfaced
              (created_at). "35m ago" should mean the operator clicked
              Approve/Reject 35 minutes ago, not that it was queued then. */}
          <span style={styles.time}>
            {showResolution && item.resolved_at
              ? formatRelativeTime(item.resolved_at)
              : formatRelativeTime(item.created_at)}
          </span>
          {/* Operator assignment chip (Task 3201a). Always render — when
              `assignedTo` is null the chip renders an "Unclaimed" pill with
              a click-to-claim affordance. Visibility is independent of
              ownership: claiming is informational + filterable, not a
              visibility gate. */}
          {ASSIGNMENT_ENABLED && (
            <AssignmentChip
              assignedTo={assignedTo ?? null}
              onAssign={onAssign}
              onUnassign={onUnassign}
            />
          )}
          {/* Quick rerun-workflow button — visible on non-history items
              when the attention item is linked to an execution. Mirrors
              the rerun affordance in AttentionInspector: opens a
              confirmation modal, then POSTs /v1/executions/{id}/retry
              and navigates to the new execution. */}
          {!showResolution && !item.resolution && item.execution_id && (
            <RerunWorkflowButton itemId={item.id} executionId={item.execution_id} />
          )}
          {/* Quick suppress button — visible on non-history items */}
          {!showResolution && !item.resolution && onSuppress && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={(e) => {
                    e.stopPropagation()
                    onSuppress()
                  }}
                  disabled={suppressPending}
                  aria-label="Suppress item"
                  style={styles.suppressButton}
                >
                  <Ban style={styles.suppressIcon} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Quick suppress — remove from queue</p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>

      {/* Subtitle row */}
      <div style={styles.subtitleRow}>
        {/* App badge — which app this item acts on. Placed first so the
            operator sees it before the subtitle text. Reuses the existing
            metadata row so no extra vertical space is consumed. */}
        {appLabel && (
          <Badge variant="outline" style={styles.appBadge} data-testid="queue-item-app">
            {appLabel}
          </Badge>
        )}
        <span style={styles.subtitle}>{item.subtitle}</span>
        {customer && (
          <Badge variant="outline" style={styles.customerBadge}>
            {customer}
          </Badge>
        )}
        {/* Canonical attention id — copyable. The user flagged the absent id
            as a bug (Task 4068a); surface it compactly on every row. The
            IdBadge stops click propagation so copying doesn't open the row. */}
        <span style={styles.idBadgeWrap}>
          <IdBadge id={getAttentionId(item)} />
        </span>
      </div>

      {/* Tool-aware preview block. For post_review_reply this gives the
          operator the reviewer, the star rating, and the start of the review
          text at a glance — no need to expand the card to triage. */}
      {hasPreview && preview && (
        <div style={styles.preview} data-testid="queue-item-preview">
          {(preview.reviewerName !== null || preview.rating !== null) && (
            <div style={styles.previewHeader}>
              <span style={styles.previewReviewer}>
                {preview.reviewerName ?? 'App Store reviewer'}
              </span>
              {preview.rating !== null && <StarRating rating={preview.rating} />}
            </div>
          )}
          {preview.headline && (
            <span style={styles.previewHeadline}>{preview.headline}</span>
          )}
          {preview.body && <p style={styles.previewBody}>{preview.body}</p>}
        </div>
      )}

      {/* Agent name */}
      {item.agent_name && (
        <span style={styles.agentName}>{item.agent_name}</span>
      )}

      {/* Resolution metadata (history items only) */}
      {showResolution && item.resolution && (
        <div style={styles.resolutionRow}>
          <Badge
            variant="outline"
            style={{
              ...styles.resolutionBadge,
              borderColor: getResolutionColor(item.resolution),
              color: getResolutionColor(item.resolution),
            }}
          >
            {item.resolution}
          </Badge>
          {item.resolved_by && (
            <span style={styles.resolvedBy}>by {item.resolved_by}</span>
          )}
          {item.resolved_at && (
            <span style={styles.resolvedAt}>{formatRelativeTime(item.resolved_at)}</span>
          )}
        </div>
      )}
    </div>
  )
}

// ============================================
// Legacy Approval Item Row (history tab)
// ============================================

export interface QueueItemRowProps {
  /** The approval item to render. */
  item: ApprovalItem
  /** Current queue filter (affects action button display). */
  filter: 'history'
  /** Current operator ID. */
  operatorId?: string
  /** Whether this item is currently expanded. */
  isExpanded?: boolean
  /** Whether this item is selected via keyboard navigation. */
  isKeyboardSelected?: boolean
  /** Whether this item just appeared (triggers highlight animation). */
  isNew?: boolean
  /** Whether this item is fading out (resolved). */
  isFadingOut?: boolean
  /** Called when the row body is clicked. */
  onItemClick?: () => void
  /** Called when claim action is triggered. */
  onClaim?: () => void
  /** Called when claim & open action is triggered. */
  onClaimAndOpen?: () => void
  /** Called when release action is triggered. */
  onRelease?: () => void
  /** Whether claim mutation is pending for this item. */
  claimPending?: boolean
  /** Whether release mutation is pending for this item. */
  releasePending?: boolean
}

/**
 * Queue item row component (legacy — for history tab).
 */
export function QueueItemRow({
  item,
  filter: _filter,
  operatorId: _operatorId,
  isExpanded,
  isKeyboardSelected,
  isNew,
  isFadingOut,
  onItemClick,
}: QueueItemRowProps) {
  const Icon = getToolIcon(item.icon)
  const isResolved = item.status === 'resolved'
  const borderColor = getColorValue(item.color)

  return (
    <div
      data-testid={`queue-item-${item.id}`}
      style={{
        ...styles.container,
        borderLeftColor: borderColor,
        opacity: isFadingOut ? 0.5 : 1,
      }}
      className={cn(
        'queue-item-row',
        isExpanded && 'queue-item-expanded',
        isKeyboardSelected && !isExpanded && 'queue-item-keyboard-selected',
        isNew && 'queue-item-new',
        !isExpanded && 'queue-item-hoverable'
      )}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('button')) return
        onItemClick?.()
      }}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onItemClick?.()
        }
      }}
    >
      {/* Top row: icon, display name, time */}
      <div style={styles.topRow}>
        <div style={styles.leftSection}>
          <Icon style={styles.toolIcon} />
          <span style={styles.title}>{item.display_name}</span>
          {item.blocking && (
            <Badge variant="warning" style={styles.blockingBadge}>
              Blocking
            </Badge>
          )}
        </div>
        <div style={styles.rightSection}>
          <span style={styles.time}>{formatRelativeTime(item.created_at)}</span>
          <Button variant="ghost" size="sm">
            View
          </Button>
        </div>
      </div>

      {/* Breadcrumb row */}
      <div style={styles.subtitleRow}>
        <span style={styles.subtitle}>{item.breadcrumb}</span>
      </div>

      {/* Trigger summary */}
      <p style={styles.summary}>&ldquo;{item.trigger_summary}&rdquo;</p>

      {/* Resolution info */}
      {isResolved && item.resolution && (
        <div style={styles.resolutionRow}>
          <ResolutionBadge resolution={item.resolution} />
          <span style={styles.resolutionMeta}>
            by {item.resolved_by ?? 'Unknown'} &middot; {item.resolved_at ? formatRelativeTime(item.resolved_at) : ''}
          </span>
        </div>
      )}
    </div>
  )
}

// ============================================
// Sub-components
// ============================================

/**
 * Quick "Rerun workflow" button. Confirms before firing
 * POST /v1/executions/{id}/retry and navigates to the new execution.
 *
 * Self-contained (owns its own confirm-modal + pending state) so the
 * row's parent doesn't have to thread a rerun callback through for
 * every queue item — this keeps the affordance scoped to rows where
 * `execution_id` is present without complicating the AttentionItemRow
 * prop surface.
 */
interface RerunWorkflowButtonProps {
  itemId: string
  executionId: string
}

function RerunWorkflowButton({ itemId, executionId }: RerunWorkflowButtonProps) {
  const [showConfirm, setShowConfirm] = useState(false)
  const [isRerunning, setIsRerunning] = useState(false)
  const { toast } = useToast()
  const operator = useOperator()
  const markItemFadingOut = useQueueStore((s) => s.markItemFadingOut)

  const handleConfirm = useCallback(async () => {
    setShowConfirm(false)
    setIsRerunning(true)
    try {
      // The backend requires reviewer_id and uses it to attribute the
      // `resolution=retried` it stamps on the original execution.
      const reviewerId = operator?.id ?? 'operator'
      await retryExecution(executionId, reviewerId)
      toast({
        title: 'Workflow rerun started',
        description:
          'A fresh execution has been queued. This attention item is now redundant and is being removed.',
      })
      // Optimistic UX: the backend marks the original execution
      // resolution=retried and the linked attention item resolves on
      // the next poll, but that round-trip can take several seconds.
      // Fade the row out immediately and drop it from the store so
      // the operator's queue reflects the action right away. If the
      // poll later disagrees, the store will reconcile.
      markItemFadingOut(itemId)
      setTimeout(() => {
        useQueueStore.setState((state) => ({
          attentionItems: state.attentionItems.filter((i) => i.id !== itemId),
          attentionCount: Math.max(0, state.attentionCount - 1),
          expandedItemId:
            state.expandedItemId === itemId ? null : state.expandedItemId,
          detailData: state.expandedItemId === itemId ? null : state.detailData,
        }))
      }, ROW_FADE_OUT_MS)
    } catch (err) {
      toast({
        title: 'Failed to rerun workflow',
        description:
          err instanceof Error
            ? err.message
            : 'The retry request did not complete. Please try again.',
      })
    } finally {
      setIsRerunning(false)
    }
  }, [executionId, itemId, markItemFadingOut, operator?.id, toast])

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => {
              e.stopPropagation()
              setShowConfirm(true)
            }}
            disabled={isRerunning}
            aria-label="Rerun workflow"
            style={styles.suppressButton}
            data-testid="queue-item-rerun-workflow-button"
          >
            {isRerunning ? (
              <Loader2 style={styles.suppressIcon} className="animate-spin" />
            ) : (
              <RotateCcw style={styles.suppressIcon} />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>Rerun workflow — restart the entire execution from the start</p>
        </TooltipContent>
      </Tooltip>
      <ConfirmModal
        open={showConfirm}
        onOpenChange={setShowConfirm}
        title="Rerun this workflow?"
        severity="warning"
        description="This will start a fresh execution of the entire workflow from the beginning. Any side effects (emails, charges, status changes) may run again. Use this when the underlying situation has changed and the lifecycle should re-evaluate from scratch."
        confirmLabel="Rerun workflow"
        onConfirm={handleConfirm}
        onCancel={() => setShowConfirm(false)}
      />
    </>
  )
}

/**
 * Operator assignment chip with dropdown for re-assignment.
 */
interface AssignmentChipProps {
  assignedTo: string | null
  onAssign?: (tag: OperatorTag) => void
  onUnassign?: () => void
}

function AssignmentChip({ assignedTo, onAssign, onUnassign }: AssignmentChipProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={assignedTo ? 'default' : 'ghost'}
          size="sm"
          style={assignedTo ? styles.assignedChip : styles.unassignedChip}
          onClick={(e) => e.stopPropagation()}
        >
          {assignedTo ?? 'Unclaimed'}
          <ChevronDown style={styles.chevron} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {OPERATOR_TAGS.map((tag) => (
          <DropdownMenuItem
            key={tag}
            onClick={() => onAssign?.(tag)}
            disabled={assignedTo === tag}
          >
            {tag}
          </DropdownMenuItem>
        ))}
        {assignedTo && (
          <DropdownMenuItem onClick={() => onUnassign?.()}>
            Remove Assignment
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/**
 * Resolution status badge for history items.
 */
function ResolutionBadge({ resolution }: { resolution: string }) {
  const variants = {
    approved: 'success',
    rejected: 'danger',
    suppressed: 'warning',
  } as const

  return <Badge variant={variants[resolution as keyof typeof variants] ?? 'outline'}>{resolution}</Badge>
}

// ============================================
// Helpers
// ============================================

/**
 * Map color names from tool UI registry to CSS variable values.
 */
function getColorValue(color: string | null | undefined): string {
  const colorMap: Record<string, string> = {
    blue: 'var(--accent-primary)',
    primary: 'var(--accent-primary)',
    green: 'var(--success)',
    success: 'var(--success)',
    yellow: 'var(--warning)',
    amber: 'var(--warning)',
    warning: 'var(--warning)',
    red: 'var(--error)',
    danger: 'var(--error)',
    gray: 'var(--border-subtle)',
    neutral: 'var(--border-subtle)',
  }

  return colorMap[color?.toLowerCase() ?? ''] ?? 'var(--border-subtle)'
}

/**
 * Format an ISO timestamp as relative time.
 */
function formatRelativeTime(isoString: string): string {
  const date = new Date(isoString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffSeconds = Math.floor(diffMs / 1000)
  const diffMinutes = Math.floor(diffSeconds / 60)
  const diffHours = Math.floor(diffMinutes / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffSeconds < 60) {
    return 'just now'
  }
  if (diffMinutes < 60) {
    return `${diffMinutes}m ago`
  }
  if (diffHours < 24) {
    return `${diffHours}h ago`
  }
  if (diffDays < 7) {
    return `${diffDays}d ago`
  }

  return date.toLocaleDateString()
}

// ============================================
// Helpers
// ============================================

/**
 * Get a color for a resolution outcome badge.
 */
function getResolutionColor(resolution: string): string {
  switch (resolution) {
    case 'approved':
      return 'hsl(142, 70%, 45%)' // green
    case 'rejected':
      return 'hsl(0, 70%, 55%)' // red
    case 'suppressed':
      return 'hsl(35, 70%, 50%)' // amber
    case 'replied':
      return 'hsl(210, 70%, 55%)' // blue
    case 'closed':
      return 'var(--text-muted)'
    default:
      return 'var(--text-muted)'
  }
}

// ============================================
// Styles
// ============================================

const styles: Record<string, CSSProperties> = {
  container: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    padding: '16px',
    // The card is itself a flex item of the queue's item list. Without an
    // explicit `min-width: 0` its default `min-width: auto` lets the widest
    // nowrap child (a long title or subtitle) set the card's width, pushing
    // the right-hand controls out of the view pane and forcing the operator
    // to scroll horizontally to reach them. Clamp to the pane and let the
    // individual text children ellipsize instead.
    minWidth: 0,
    maxWidth: '100%',
    overflow: 'hidden',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--border-subtle)',
    borderLeftWidth: '4px',
    background: 'var(--bg-secondary)',
    cursor: 'pointer',
    // Explicit property list avoids `all` catching every layout shift
    // (gap, padding) and stuttering the hover state.
    transition:
      'background-color var(--transition-fast), border-color var(--transition-fast), box-shadow var(--transition-fast)',
  },
  topRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    minWidth: 0,
  },
  leftSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flex: 1,
    minWidth: 0,
  },
  toolIcon: {
    width: '20px',
    height: '20px',
    flexShrink: 0,
    color: 'var(--text-muted)',
  },
  typeBadge: {
    fontSize: '10px',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    flexShrink: 0,
  },
  title: {
    fontWeight: 500,
    fontSize: '14px',
    color: 'var(--text-primary)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  blockingBadge: {
    fontSize: '10px',
    textTransform: 'uppercase' as const,
  },
  rightSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexShrink: 0,
  },
  time: {
    fontSize: '12px',
    color: 'var(--text-muted)',
  },
  subtitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    minWidth: 0,
  },
  subtitle: {
    fontSize: '12px',
    color: 'var(--text-muted)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
    // Allow the subtitle to shrink so the badges and the id badge that
    // follow it stay inside the card rather than being pushed out.
    minWidth: 0,
    flexShrink: 1,
  },
  customerBadge: {
    fontSize: '10px',
    flexShrink: 0,
  },
  appBadge: {
    fontSize: '10px',
    fontWeight: 600,
    letterSpacing: '0.01em',
    flexShrink: 0,
    color: 'var(--accent-primary)',
    borderColor: 'var(--accent-primary)',
  },
  idBadgeWrap: {
    marginLeft: 'auto',
    flexShrink: 0,
  },
  agentName: {
    fontSize: '11px',
    color: 'var(--text-muted)',
    opacity: 0.7,
  },
  preview: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    marginTop: '2px',
    paddingTop: '8px',
    borderTop: '1px solid var(--border-subtle)',
    minWidth: 0,
  },
  previewHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  previewReviewer: {
    fontSize: '13px',
    fontWeight: 600,
    color: 'var(--text-primary)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  previewHeadline: {
    fontSize: '12px',
    fontWeight: 500,
    color: 'var(--text-secondary)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  previewBody: {
    margin: 0,
    fontSize: '13px',
    lineHeight: 1.45,
    color: 'var(--text-muted)',
    // Clamp to two lines so a long review can't dominate the row height.
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical' as const,
    overflow: 'hidden',
    // A long unbroken token (an id, a URL) would otherwise widen the card
    // past the pane, since the line clamp caps height but not width.
    overflowWrap: 'anywhere' as const,
    minWidth: 0,
  },
  summary: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
    fontSize: '14px',
    color: 'var(--text-muted)',
  },
  resolutionRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginTop: '4px',
    fontSize: '12px',
  },
  resolutionMeta: {
    color: 'var(--text-muted)',
  },
  resolutionBadge: {
    fontSize: '10px',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    flexShrink: 0,
  },
  resolvedBy: {
    fontSize: '12px',
    color: 'var(--text-muted)',
  },
  resolvedAt: {
    fontSize: '12px',
    color: 'var(--text-muted)',
    opacity: 0.7,
  },
  assignedChip: {
    fontSize: '11px',
    padding: '2px 8px',
    height: '24px',
  },
  unassignedChip: {
    fontSize: '11px',
    padding: '2px 8px',
    height: '24px',
    opacity: 0.6,
  },
  chevron: {
    width: '14px',
    height: '14px',
    marginLeft: '2px',
  },
  suppressButton: {
    width: '28px',
    height: '28px',
    padding: 0,
    flexShrink: 0,
    opacity: 0.5,
    color: 'var(--text-muted)',
  },
  suppressIcon: {
    width: '14px',
    height: '14px',
  },
}

export default QueueItemRow
